import type { AppStore } from '../core/store';
import type { AppState, AttributeFilter, AttributeFilterOperator, AttributeValue, FieldDescriptor } from '../core/types';
import {
  attributeFilterCatalog,
  attributeFilterFieldLabel,
  attributeFilterValueLabel,
  CATEGORICAL_FILTER_OPERATORS,
  createAttributeFilter,
  NUMERIC_FILTER_OPERATORS,
  validateAttributeFilter,
} from '../domain/attributeFilters';
import { normalizedFieldName } from '../domain/attributes';
import { emptyWorkScope } from '../domain/scope';
import { icon } from './icons';

const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const normalized = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
const valueKey = (value: AttributeValue): string => value === null ? 'null:' : `${typeof value}:${String(value)}`;
const OPERATOR_LABELS: Readonly<Record<AttributeFilterOperator, string>> = { 'one-of': 'es', 'not-one-of': 'no es', eq: '=', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'entre' };
const PREFERRED = ['zona', 'zon', 'variedad', 'estatus', 'estado', 'tch', 'productor', 'tiporiego', 'trie', 'renovacion', 'textura', 'edad'];
const VALUE_LIMIT = 200;

/** Short chip text: "Zona: Norte", "Estatus: Pendiente +1", "TCH ≥ 80". */
export function filterChipLabel(filter: AttributeFilter, field: FieldDescriptor | undefined): string {
  const name = field ? attributeFilterFieldLabel(field) : 'Campo no disponible';
  if (filter.operator === 'one-of' || filter.operator === 'not-one-of') {
    if (!filter.values.length) return `${name}: elegir valores`;
    const first = attributeFilterValueLabel(filter.values[0]!);
    return `${name}${filter.operator === 'not-one-of' ? ' no es' : ':'} ${first}${filter.values.length > 1 ? ` +${filter.values.length - 1}` : ''}`;
  }
  if (filter.value === undefined) return `${name}: definir valor`;
  const format = (value: number | undefined): string => value === undefined ? '…' : value.toLocaleString('es-NI', { maximumFractionDigits: 4 });
  return filter.operator === 'between' ? `${name} entre ${format(filter.value)} y ${format(filter.upperValue)}` : `${name} ${OPERATOR_LABELS[filter.operator]} ${format(filter.value)}`;
}

/**
 * Filters as chips above the map plus a "+ Filtro" popover. Filters narrow what is visible (map, table,
 * explorer, counts) and never create or alter the selection. AttributeFilterSet stays the single model.
 */
export class FilterBar {
  private editing: string | null = null;
  private picking = false;
  private fieldQuery = '';
  private valueQuery = '';

  constructor(private readonly chips: HTMLElement, private readonly popover: HTMLElement, private readonly store: AppStore) {
    chips.addEventListener('click', (event) => {
      const target = event.target as Element;
      const remove = target.closest<HTMLElement>('[data-filter-remove]');
      if (remove) { event.stopPropagation(); this.apply(this.filters().filter(({ id }) => id !== remove.dataset.filterRemove)); if (this.editing === remove.dataset.filterRemove) this.close(); return; }
      const chip = target.closest<HTMLElement>('[data-filter-chip]');
      if (chip) this.edit(chip.dataset.filterChip!);
    });
    popover.addEventListener('click', (event) => this.onPopoverClick(event));
    popover.addEventListener('input', (event) => this.onPopoverInput(event));
    popover.addEventListener('change', (event) => this.onPopoverChange(event));
    document.addEventListener('pointerdown', (event) => {
      if (this.popover.hidden) return;
      const target = event.target as Element;
      if (this.popover.contains(target) || target.closest?.('#filter-add-button, [data-filter-chip], [data-command="filter-open"]')) return;
      this.close();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !this.popover.hidden) { this.close(); event.preventDefault(); } });
  }

  /** "+ Filtro": choose a field. */
  open(): void {
    if (!this.store.getState().model) { this.store.update({ error: 'Carga el Maestro CASUR para filtrar.' }, { markDirty: false }); return; }
    this.picking = true; this.editing = null; this.fieldQuery = ''; this.popover.hidden = false; this.renderPopover();
    this.popover.querySelector<HTMLInputElement>('[data-filter-field-search]')?.focus();
  }

  edit(id: string): void { this.picking = false; this.editing = id; this.valueQuery = ''; this.popover.hidden = false; this.renderPopover(); }

  close(): void { this.popover.hidden = true; this.picking = false; this.editing = null; }

  render(state: AppState): void {
    const catalog = attributeFilterCatalog(state.model, emptyWorkScope(), state.operationalFields);
    this.chips.innerHTML = state.attributeFilters.map((filter) => {
      const validation = validateAttributeFilter(state.model, filter, state.operationalFields);
      const field = validation.field ?? catalog.fields.find(({ id }) => id === filter.fieldId);
      const label = filterChipLabel(filter, field);
      return `<span class="chip filter-chip${validation.valid ? '' : ' invalid'}" data-filter-chip="${esc(filter.id)}" role="button" tabindex="0" title="${esc(validation.valid ? 'Editar filtro' : validation.reason ?? 'Filtro incompleto')}">${icon('filter')}<span>${esc(label)}</span><button type="button" class="chip-remove" data-filter-remove="${esc(filter.id)}" aria-label="Quitar filtro ${esc(label)}">${icon('close')}</button></span>`;
    }).join('');
    const add = document.querySelector<HTMLButtonElement>('#filter-add-button');
    if (add) add.disabled = !state.model;
    if (!this.popover.hidden && this.editing && !state.attributeFilters.some(({ id }) => id === this.editing)) this.close();
    else if (!this.popover.hidden && this.editing && !this.popover.contains(document.activeElement)) this.renderPopover();
  }

  private filters(): AppState['attributeFilters'] { return this.store.getState().attributeFilters; }

  private renderPopover(): void {
    const state = this.store.getState();
    const catalog = attributeFilterCatalog(state.model, emptyWorkScope(), state.operationalFields);
    if (this.picking) {
      const query = normalized(this.fieldQuery);
      const rank = (field: FieldDescriptor): number => { const index = PREFERRED.indexOf(normalizedFieldName(field.name)); return field.source === 'join' ? -2 : field.source === 'project' ? -1 : index < 0 ? 99 : index; };
      const fields = catalog.fields.filter((field) => !query || normalized(attributeFilterFieldLabel(field)).includes(query)).sort((a, b) => rank(a) - rank(b));
      this.popover.innerHTML = `<h3>Filtrar por</h3><input type="search" data-filter-field-search placeholder="Buscar campo" value="${esc(this.fieldQuery)}" aria-label="Buscar campo" /><div class="filter-field-list">${fields.map((field) => `<button type="button" data-filter-pick="${esc(field.id)}"><span>${esc(attributeFilterFieldLabel(field))}</span><small>${field.source === 'join' ? 'Excel' : field.source === 'project' ? 'Proyecto' : field.type === 'number' ? 'Número' : ''}</small></button>`).join('') || '<small class="pane-note">Sin campos.</small>'}</div>`;
      return;
    }
    const filter = this.filters().find(({ id }) => id === this.editing); if (!filter) { this.close(); return; }
    const validation = validateAttributeFilter(state.model, filter, state.operationalFields);
    const field = validation.field ?? catalog.fields.find(({ id }) => id === filter.fieldId);
    const numeric = field?.type === 'number';
    const operators = numeric ? NUMERIC_FILTER_OPERATORS : CATEGORICAL_FILTER_OPERATORS;
    const values = this.valueOptions(state, filter);
    const selected = new Set(filter.values.map(valueKey));
    const controls = !field ? '' : numeric
      ? `<div class="filter-numbers"><input type="number" step="any" data-filter-number="lower" value="${filter.value ?? ''}" aria-label="Valor" />${filter.operator === 'between' ? `<span>y</span><input type="number" step="any" data-filter-number="upper" value="${filter.upperValue ?? ''}" aria-label="Valor final" />` : ''}</div>`
      : `${values.total > 12 ? `<input type="search" data-filter-value-search placeholder="Buscar valores" value="${esc(this.valueQuery)}" aria-label="Buscar valores" />` : ''}<div class="filter-values">${values.visible.map((value, index) => `<label><input type="checkbox" data-filter-value="${index}" ${selected.has(valueKey(value)) ? 'checked' : ''}/><span>${esc(attributeFilterValueLabel(value))}</span></label>`).join('') || '<small class="pane-note">Sin valores.</small>'}</div>${values.matching > values.visible.length ? `<small class="pane-note">Mostrando ${values.visible.length} de ${values.matching}. Busca para acotar.</small>` : ''}`;
    this.popover.innerHTML = `<div class="filter-editor"><h3>${esc(field ? attributeFilterFieldLabel(field) : 'Campo no disponible')}</h3><select data-filter-operator aria-label="Condición">${operators.map((operator) => `<option value="${operator}" ${operator === filter.operator ? 'selected' : ''}>${operator === 'one-of' ? 'Es cualquiera de' : operator === 'not-one-of' ? 'No es ninguno de' : OPERATOR_LABELS[operator]}</option>`).join('')}</select>${controls}${validation.valid ? '' : `<small class="field-warning">${esc(validation.reason ?? 'Filtro incompleto.')}</small>`}<div class="filter-footer"><button type="button" class="link-button" data-filter-remove-current>Quitar filtro</button><button type="button" class="button button-primary" data-filter-done>Listo</button></div></div>`;
  }

  private valueOptions(state: AppState, filter: AttributeFilter): { readonly visible: readonly AttributeValue[]; readonly total: number; readonly matching: number } {
    const all = attributeFilterCatalog(state.model, emptyWorkScope(), state.operationalFields).valuesByFieldId.get(filter.fieldId) ?? [];
    const merged = [...all, ...filter.values.filter((value) => !all.some((candidate) => valueKey(candidate) === valueKey(value)))];
    const query = normalized(this.valueQuery);
    const matching = query ? merged.filter((value) => normalized(attributeFilterValueLabel(value)).includes(query)) : merged;
    const selected = new Set(filter.values.map(valueKey));
    const ordered = [...merged.filter((value) => selected.has(valueKey(value))), ...matching.filter((value) => !selected.has(valueKey(value)))];
    return { visible: ordered.slice(0, VALUE_LIMIT), total: merged.length, matching: matching.length };
  }

  private onPopoverClick(event: Event): void {
    const target = event.target as Element;
    const pick = target.closest<HTMLElement>('[data-filter-pick]');
    if (pick) {
      const state = this.store.getState();
      const field = attributeFilterCatalog(state.model, emptyWorkScope(), state.operationalFields).fields.find(({ id }) => id === pick.dataset.filterPick); if (!field) return;
      const filter = createAttributeFilter(field);
      this.apply([...this.filters(), filter]);
      this.edit(filter.id);
      return;
    }
    if (target.closest('[data-filter-done]')) { this.close(); return; }
    if (target.closest('[data-filter-remove-current]') && this.editing) { const id = this.editing; this.close(); this.apply(this.filters().filter((filter) => filter.id !== id)); }
  }

  private onPopoverInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.matches('[data-filter-field-search]')) { this.fieldQuery = target.value; this.renderPopover(); this.refocus('[data-filter-field-search]'); return; }
    if (target.matches('[data-filter-value-search]')) { this.valueQuery = target.value; this.renderPopover(); this.refocus('[data-filter-value-search]'); return; }
    if (target.matches('[data-filter-number]')) this.patchNumber(target);
  }

  private onPopoverChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const filter = this.filters().find(({ id }) => id === this.editing); if (!filter) return;
    if (target.matches('[data-filter-operator]')) { this.replace({ ...filter, operator: target.value as AttributeFilterOperator, values: [], value: undefined, upperValue: undefined }); this.renderPopover(); return; }
    if (target.matches('[data-filter-value]')) {
      const value = this.valueOptions(this.store.getState(), filter).visible[Number((target as HTMLInputElement).dataset.filterValue)];
      if (value === undefined) return;
      const values = new Map(filter.values.map((item) => [valueKey(item), item]));
      if ((target as HTMLInputElement).checked) values.set(valueKey(value), value); else values.delete(valueKey(value));
      this.replace({ ...filter, values: [...values.values()] });
      const list = this.popover.querySelector('.filter-values'); const scroll = list?.scrollTop ?? 0; const label = attributeFilterValueLabel(value);
      this.renderPopover();
      const restored = this.popover.querySelector('.filter-values'); if (restored) restored.scrollTop = scroll;
      [...this.popover.querySelectorAll<HTMLInputElement>('[data-filter-value]')].find((input) => input.parentElement?.textContent === label)?.focus();
    }
  }

  private patchNumber(input: HTMLInputElement): void {
    const filter = this.filters().find(({ id }) => id === this.editing); if (!filter) return;
    const raw = input.value.trim(); const value = raw === '' ? undefined : Number(raw);
    if (value !== undefined && !Number.isFinite(value)) return;
    this.replace(input.dataset.filterNumber === 'upper' ? { ...filter, upperValue: value } : { ...filter, value });
  }

  private refocus(selector: string): void {
    const input = this.popover.querySelector<HTMLInputElement>(selector);
    input?.focus(); input?.setSelectionRange(input.value.length, input.value.length);
  }

  private replace(next: AttributeFilter): void { this.apply(this.filters().map((filter) => filter.id === next.id ? next : filter)); }

  /** Filters never touch the selection; they only change the visible universe. */
  private apply(filters: AppState['attributeFilters']): void { this.store.update({ attributeFilters: filters }); }
}
